
import Vue, { PropType } from 'vue';
import customersAPI from '@/services/CustomersService';
import {
  CvGrid,
  CvColumn,
  CvRow,
  CvAccordion,
  CvAccordionItem,
} from '@carbon/vue';
import {
  PrintTechnique,
  PrintTechniqueGroup,
  PrintPositionsManagerHeader,
  PrintPositionsManagerFooter,
  PrintTechniqueImage,
} from './components';

import { mapActions, mapState, mapStores } from 'pinia';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useLangStore } from '@/stores/v2/lang';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { ProductPrintPositionAddModel } from '@/typings';
import { PrintTechnique as PrintTechniqueType } from '@midocean/webapps-shared-domain';

export default Vue.extend({
  components: {
    PrintTechnique,
    PrintTechniqueGroup,
    PrintPositionsManagerHeader,
    PrintPositionsManagerFooter,
    PrintTechniqueImage,
    CvGrid,
    CvRow,
    CvColumn,
    CvAccordion,
    CvAccordionItem,
  },
  props: {
    value: {
      type: [] as PropType<ProductPrintPositionAddModel[]>,
      required: true,
    },
    /**
     * When editId is provided, we know that we are in EDIT mode
     */
    editId: {
      type: String,
      required: true,
    },
    productSku: {
      type: String,
      required: false,
    },
    variantSku: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      internalModel: [] as ProductPrintPositionAddModel[],
      currentEditingItemId: this.editId,
      /**
       * Flag to tell if we should show the warning message
       * In cases when position is about to be switched -
       * (does not apply when switching techniques within the same position)
       */
      isWarningShown: false,
      lastSwap: {} as { from: string; to: string },
      end: 'end',
    };
  },
  computed: {
    ...mapState(useProductStore, [
      'data',
      'lastManagedPrintPositionId',
      'selectedVariant',
    ]),
    ...mapStores(useProductStore),
    ...mapState(useLangStore, ['languageInUse']),
    printPositionsComputed():
      | { id: string; positionName: string; enabled: boolean }[]
      | undefined {
      return (
        this.data.productPrintPositions &&
        this.data.productPrintPositions.map((position) => {
          const {
            id,
            name,
            printTechniques,
            maxWidthMm,
            maxHeightMm,
            readyToPrint,
          } = position;

          const getTechniqueTranslation = (
            lang: string,
            technique: PrintTechniqueType
          ) => {
            return technique.printTechnique.nameTranslations.find(
              ({ language }) => language.code === lang
            )?.text;
          };

          return {
            id,
            positionName: name,
            options: printTechniques.map((technique) => ({
              id: technique.id,
              maximumColours: technique.maximumColours,
              isFullColor: technique.printTechnique.isFullColor,
              printTechniqueSAPCode: technique.printTechnique.code,
              techniqueName:
                getTechniqueTranslation(this.languageInUse, technique) ||
                getTechniqueTranslation('en', technique),
              groupCode: technique.printTechnique.group.code,
              readyToPrint: technique.readyToPrint,
            })),
            dimensions: {
              width: maxWidthMm,
              height: maxHeightMm,
            },
            cover:
              this.data.variants
                .find((v) => v.sku === this.variantSku)
                ?.images.find((img) => img.printPositionID === id)
                ?.imageThumbnailUrl || '',
            enabled: !this.value.some(({ positionId }) => positionId === id),
            readyToPrint,
          };
        })
      );
    },

    isAddEnabled(): boolean {
      if (this.editId) {
        if (this.printPositionsComputed) {
          let selectablePositionIds = this.printPositionsComputed
            .filter((x) => x.enabled || x.id === this.editId)
            .map((position) => position.id);
          return (
            this.internalModel.filter(
              (x) => selectablePositionIds.lastIndexOf(x.positionId) !== -1
            ).length > 0
          );
        } else {
          return false;
        }
      } else {
        return (
          /**
           * Do we have any free positions left to select/add!?
           */

          this.data.productPrintPositions?.length > this.value.length &&
          /**
           * Do we have selected something, other than previously selected and
           * now disabled ones!?
           */
          this.internalModel.length - this.value.length > 0
        );
      }
    },
  },
  async mounted() {
    this.internalModel = [...this.value];
  },

  methods: {
    log: console.log,
    ...mapActions(useProductStore, ['getProduct']),
    ...mapActions(useShapeStore, ['reassignRefs']),
    ...mapActions(useMirrorStore, ['reassignMirrorShapes']),

    saveSwap({ from, to }: { from: string; to: string }) {
      this.lastSwap = {
        from,
        to,
      };
    },

    updateModel(value: ProductPrintPositionAddModel[]) {
      /**
       * Reassing shape references when switching positions
       */
      if (this.lastSwap.to) {
        this.reassignRefs(this.lastSwap as { from: string; to: string });
        this.reassignMirrorShapes(
          this.lastSwap as { from: string; to: string }
        );
      }

      /**
       * Send model updates to parent
       */
      this.$emit('print-position:action', [...value], this.lastSwap);
      /**
       * Close the modal after edit/add action
       */

      this.$modal.hide('print-positions-modal');
      //this.$emit('print-position:cancel');
    },

    updateInternalModel(value: ProductPrintPositionAddModel) {
      /**
       * If editId is available we are in edit mode
       * so we enter a position switching logic
       */
      if (this.editId) {
        /**
         * Keep the reference to the last managed print position
         */
        this.productV2Store.$patch({
          lastManagedPrintPositionId: value.positionId,
        });

        /**
         * When we deviate from the original item, we began editing with
         * we issue a warning that some unpredictable behaviour may occur
         */

        this.isWarningShown = this.editId !== value.positionId;

        const swapFrom = this.internalModel.find((m) => {
          return m.positionId === this.currentEditingItemId;
        });

        const swapTo = { ...value };
        this.currentEditingItemId = value.positionId;

        if (swapFrom) {
          this.internalModel.splice(
            this.internalModel.findIndex(
              (im) => im.positionId === swapFrom?.positionId
            ),
            1,
            swapTo
          );
        } else {
          this.internalModel.push(value);
        }

        /**
         * Keep reference to last swap so we can reassign logo refs for edited
         * position
         */
        this.saveSwap({
          from: this.editId,
          to: swapTo.positionId,
        });

        return;
      }
      /**
       * End edit mode
       */

      /**
       * Begin add mode
       */
      const modelIndex = this.internalModel.findIndex(
        (position) => position.positionName === value.positionName
      );
      if (modelIndex > -1) {
        /**
         * Position exists, we update the model
         */
        this.internalModel.splice(modelIndex, 1, value);
      } else {
        /**
         * Otherwise we just add the new model to the list
         */
        this.internalModel.push(value);
      }
    },
    clearTechniqueSelection(value: ProductPrintPositionAddModel) {
      const modelIndex = this.internalModel.findIndex(
        (position) => position.positionName === value.positionName
      );
      if (modelIndex > -1) {
        this.internalModel = this.internalModel.filter(
          (model) => model.positionId !== value.positionId
        );
      }
    },
  },
  watch: {
    value(val) {
      this.internalModel = [...val];
    },
    async languageInUse() {
      if (process.env.VUE_APP_USE_LOCAL_TRANSLATIONS === 'false') {
        var translationsJson = await customersAPI.getTranslations(
          this.languageInUse
        );
        this.$i18n.setLocaleMessage(this.languageInUse, translationsJson.data);
      }
    },
  },
});
